/**
 * VoidMovementDialog — confirm dialog for `cashMovements.reverse`.
 *
 * Mirrors `features/invoices/void-invoice-dialog.tsx` style — danger
 * intent + mandatory reason (min 3 chars matches the wire schema). The
 * reversal posts as a new append-only row per ADR-0012 §3.
 *
 * Used by the voucher and transfer detail pages; the title + kind label
 * are passed in so the same component covers both kinds.
 */
import { useEffect, useState } from "react";
import { TriangleAlert } from "lucide-react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
} from "@flint/ui";

export interface VoidMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Modal title; matches the destructive button's verb. */
  title: string;
  /** Formatted amount like "−₹1,200" — surfaced as a chip in the body. */
  amountLabel: string;
  /** Pretty kind label, e.g. "Petty cash voucher" or "Transfer". */
  kindLabel: string;
  pending?: boolean;
  errorMessage?: string;
  /** Fires only when reason passes the min-length client guard. */
  onConfirm: (reason: string) => void;
}

const MIN_REASON_LEN = 3;

export function VoidMovementDialog({
  open,
  onOpenChange,
  title,
  amountLabel,
  kindLabel,
  pending,
  errorMessage,
  onConfirm,
}: VoidMovementDialogProps) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LEN;
  const showInline = touched && tooShort;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (tooShort) return;
    onConfirm(trimmed);
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="inline-grid place-items-center h-9 w-9 rounded-md bg-rose-50 text-rose-700 shrink-0"
              >
                <TriangleAlert size={16} />
              </span>
              <div className="flex-1">
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>
                  Voiding this {kindLabel.toLowerCase()} ({amountLabel}) posts a new reversal row
                  per ADR-0012 §3. The original stays in history so the audit trail is preserved.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-1.5 mt-4">
            <Label htmlFor="void-movement-reason-input">Reason</Label>
            <Input
              id="void-movement-reason-input"
              autoFocus
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setTouched(true);
              }}
              maxLength={500}
              placeholder="e.g. Posted to the wrong drawer."
              aria-invalid={showInline}
              aria-describedby={showInline ? "void-movement-reason-error" : undefined}
            />
            {showInline ? (
              <p id="void-movement-reason-error" className="text-sm text-bad">
                Reason must be at least {MIN_REASON_LEN} characters.
              </p>
            ) : (
              <p className="text-sm text-muted">Lands in the audit log alongside the reversal.</p>
            )}
            {errorMessage ? <p className="text-sm text-bad mt-1">{errorMessage}</p> : null}
          </div>

          <div className="flex items-center justify-end gap-2 mt-5">
            <Button
              type="button"
              intent="ghost"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button type="submit" intent="danger" disabled={pending || tooShort}>
              {pending ? "Voiding…" : "Void"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
